import { ReactNode } from 'react'
import { ListingCard } from './ListingCard'
import { ListingCardSkeleton } from '@/components/common/Skeleton'
import { EmptyState } from '@/components/common/EmptyState'
import type { Listing } from '@/types/listing'

interface ListingGridProps {
  listings: Listing[]
  isLoading?: boolean
  emptyMessage?: string
  emptyState?: ReactNode
  columns?: 2 | 3 | 4
  skeletonCount?: number
  hideBanner?: boolean
}

export function ListingGrid({
  listings,
  isLoading = false,
  emptyMessage = 'No listings found',
  emptyState,
  columns = 4,
  skeletonCount = 12,
  hideBanner = false,
}: ListingGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  }

  if (isLoading) {
    return (
      <div className={`grid ${gridCols[columns]} gap-4 md:gap-6`}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <ListingCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (listings.length === 0) {
    if (emptyState) {
      return <>{emptyState}</>
    }
    return (
      <EmptyState
        variant="no-results"
        description={emptyMessage}
      />
    )
  }

  return (
    <div className={`grid ${gridCols[columns]} gap-4 md:gap-6`}>
      {listings.map((listing, i) => (
        <div
          key={listing.id}
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: `${Math.min(i * 0.05, 0.6)}s` }}
        >
          <ListingCard listing={listing} hideBanner={hideBanner} />
        </div>
      ))}
    </div>
  )
}
